import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Play, ExternalLink, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';

// Image imports
const dontBeABabyImage = '/images/games/dont-be-a-baby-cover.jpg';
const gameGauntletImage = '/images/logos/gg-sim-jam-hero.jpg'; // SIM Jam key art (landscape)
const goosebumpsImage = '/images/games/goosebumps-game.jpg';
const newGoosebumpsNightOfScaresImage = '/images/games/goosebumps-night-scares.jpg';
const backgroundImage = '/images/global/CF-sci-fi-neon_v1.6.sales.16x9@2x.jpg'; // Cyberpunk sci-fi background







export function Games() {
  const [currentGame, setCurrentGame] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const games = useMemo(() => ([
    {
      title: "DON'T BE A BABY!",
      titleLines: ["DON'T BE A", "BABY!"], // match the cover art instead of one word per line
      subtitle: "SIDE-SCROLLING RUN & GUN ADVENTURE",
      description: "With a hilarious narrative, players take on the role of Max Murphy, a baby who has finally had enough of his neglectful mother and decides to venture out into the world on his own. As he navigates through the city's nightlife, Max must use his wits and his trusty bottle weapon to defend himself against a variety of colorful enemies, including drunken partygoers, strippers, and other unsavory characters. Each level is focused on a boss fight, with a skateboarding level and a flying level adding some variety. Developed by David Caldera.",
      fullDescription: "A HUMOROUS AND ACTION-PACKED SIDE-SCROLLING RUN AND GUN GAME WITH A TWIST: YOUR BOTTLE WEAPON SHOOTS IN DIFFERENT PATTERNS!",
      rating: "",
      emmyNominees: "RE-RELEASED BY COSMIC FORCES · SEPTEMBER 2026",
      imdbRating: "",
      genre: "Platformer, Run & Gun",
      studio: "COSMIC FORCES",
      status: "Play on Steam",
      statusColor: "from-green-500 to-emerald-600",
      quote: "",
      quoteAuthor: "",
      release: "2026",
      statusLink: "https://store.steampowered.com/app/1503110/Dont_Be_A_Baby/",
      image: dontBeABabyImage,
      accentColor: "from-cyan-400 to-purple-600"
    },
    {
      title: "GAME GAUNTLET",
      subtitle: "WORLDWIDE ONLINE SIM JAM & GAME NIGHT SANTA FE",
      description: "Build a game that simulates a system — real, invented, or ridiculous. Compete for $1,000 first place, $500 second, trophies and CraftPix memberships, judged by industry experts including Jason Della Rocca and Jared Yeager (CAA · FRAME1GAMES). Then join Game Night at Vital Spaces, Santa Fe on November 21: tournaments, board games and speed painting — free, all ages.",
      fullDescription: "A SIX-WEEK, WORLDWIDE JAM TO CONNECT, LEARN AND PROMOTE YOUR WORK",
      rating: "",
      emmyNominees: "INDIE GAMES ACCELERATOR & COMMUNITY",
      imdbRating: "",
      genre: "Sept 23 – Nov 4",
      studio: "COSMIC FORCES",
      status: "Join the SIM Jam",
      statusColor: "from-green-500 to-emerald-600",
      quote: "",
      quoteAuthor: "",
      release: "Nov 21",
      genreLabel: "SIM Jam",
      releaseLabel: "Game Night",
      statusLink: "https://www.gamegauntlet.gg/",
      image: gameGauntletImage,
      accentColor: "from-red-400 to-orange-600"
    },
    {
      title: "GOOSEBUMPS DEAD OF NIGHT",
      titleLines: ["GOOSEBUMPS", "DEAD OF NIGHT"],
      subtitle: "FIRST-PERSON SPOOKY SURVIVAL ADVENTURE",
      description: "The best-selling Goosebumps franchise has been reimagined as a first-person spooky survival adventure game for Consoles and PC. Slappy the Dummy has unleashed a nightmare of Goosebumps monsters, and they're all out to get you!",
      fullDescription: "A TERRIFYING FIRST-PERSON SURVIVAL EXPERIENCE BRINGING GOOSEBUMPS TO LIFE",
      rating: 4.7,
      emmyNominees: "FIRST PERSON HORROR ADVENTURE",
      imdbRating: "one of the most enjoyable horror games I've played this year - Metacritic",
      genre: "Horror Survival",
      studio: "COSMIC FORCES",
      status: "Available Now",
      statusColor: "from-green-500 to-emerald-600",
      quote: "\"Well‑crafted and genuinely fun… Good graphics…Jack Black\"",
      quoteAuthor: "NINTENDO LIFE",
      release: "2020",
      statusLink: "https://www.goosebumpsgame.com/",
      image: goosebumpsImage,
      accentColor: "from-purple-400 to-red-600"
    },
    {
      title: "GOOSEBUMPS NIGHT OF SCARES",
      titleLines: ["GOOSEBUMPS", "NIGHT OF SCARES"],
      subtitle: "GOOSEBUMPS' GREATEST MONSTERS COME TO LIFE!",
      description: "Goosebumps' greatest monsters come to life, including Slappy the Dummy, evil gnomes, werewolves, and more. Featuring Jack Black as the Voice of R.L. Stine.",
      fullDescription: "Mystery, madness, and frightening moments await!",
      rating: 4.3,
      emmyNominees: "Featured by Apple and GooglePlay",
      imdbRating: "Ranked #1 Apple AppStore Paid Games Oct. 2015",
      genre: "Survival Horror Adventure",
      studio: "COSMIC FORCES",
      status: "Available Now",
      statusColor: "from-green-500 to-emerald-600",
      quote: "\"...is sure to give anyone the chills.\"",
      quoteAuthor: "POPZARA",
      release: "2015",
      statusLink: "https://www.goosebumpsgame.com/",
      image: newGoosebumpsNightOfScaresImage,
      accentColor: "from-purple-400 to-red-600"
    }
  ]), []);

  const scrollToGame = (index: number) => {
    if (isScrolling) return;
    setIsScrolling(true);
    setCurrentGame(index);
    
    const gameElement = document.getElementById(`game-${index}`);
    if (gameElement) {
      gameElement.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
    
    setTimeout(() => setIsScrolling(false), 1000);
  };

  const nextGame = () => {
    const next = (currentGame + 1) % games.length;
    scrollToGame(next);
  };

  const prevGame = () => {
    const prev = (currentGame - 1 + games.length) % games.length;
    scrollToGame(prev);
  };

  // Handle scroll events to update current game
  useEffect(() => {
    const handleScroll = () => {
      if (isScrolling) return;
      
      const gameElements = games.map((_, index) => 
        document.getElementById(`game-${index}`)
      );
      
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      
      gameElements.forEach((element, index) => {
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementCenter = rect.top + window.scrollY + rect.height / 2;
          
          if (Math.abs(scrollPosition - elementCenter) < window.innerHeight / 2) {
            setCurrentGame(index);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolling, games]);

  return (
    <section id="games" className="relative">
      {/* Navigation Indicators */}
      <div className="fixed right-6 top-1/2 transform -translate-y-1/2 z-50 space-y-3 hidden lg:block">
        {games.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollToGame(index)}
            className={`block w-3 h-3 rounded-full transition-all duration-300 ${
              currentGame === index 
                ? 'bg-cyan-400 scale-125' 
                : 'bg-gray-600 hover:bg-gray-400'
            }`}
            aria-label={`Go to game ${index + 1}`}
          />
        ))}
      </div>

      {/* Navigation Arrows */}
      <div className="fixed right-6 bottom-20 z-50 space-y-2 hidden lg:block">
        <button
          onClick={prevGame}
          className="block p-2 bg-slate-800/80 backdrop-blur-sm border border-cyan-500/30 rounded-full text-cyan-400 hover:bg-cyan-500/10 transition-all duration-300"
          aria-label="Previous game"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
        <button
          onClick={nextGame}
          className="block p-2 bg-slate-800/80 backdrop-blur-sm border border-cyan-500/30 rounded-full text-cyan-400 hover:bg-cyan-500/10 transition-all duration-300"
          aria-label="Next game"
        >
          <ChevronDown className="h-5 w-5" />
        </button>
      </div>

      {/* Game Sections */}
      {games.map((game, index) => (
        <div
          key={index}
          id={`game-${index}`}
          className="relative min-h-screen flex items-center justify-center overflow-hidden lg:py-0 py-16 lg:mb-0 mb-8"
          // 🔥 HIGHLIGHTED: Background image usage with the new darkened image
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        >
          {/* Enhanced Cyberpunk Background Effects */}
          <div className="absolute inset-0">
            {/* Base cyberpunk grid pattern */}
            <div className="absolute inset-0 opacity-30">
              <div 
                className="w-full h-full"
                style={{
                  backgroundImage: `
                    linear-gradient(rgba(34, 211, 238, 0.15) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(34, 211, 238, 0.15) 1px, transparent 1px),
                    linear-gradient(rgba(168, 85, 247, 0.1) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(168, 85, 247, 0.1) 1px, transparent 1px)
                  `,
                  backgroundSize: '60px 60px, 60px 60px, 20px 20px, 20px 20px',
                  backgroundPosition: '0 0, 0 0, 0 0, 0 0'
                }}
              ></div>
            </div>

            {/* Enhanced neon circuit patterns */}
            <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-r from-transparent via-cyan-300/20 to-transparent transform -skew-y-1 shadow-lg shadow-cyan-400/20"></div>
            <div className="absolute bottom-0 right-0 w-full h-32 bg-gradient-to-l from-transparent via-orange-300/20 to-transparent transform skew-y-1 shadow-lg shadow-orange-400/20"></div>
            
            {/* Enhanced animated light rays */}
            <div className="absolute top-0 left-1/4 w-1 h-full bg-gradient-to-b from-cyan-400/30 via-transparent to-cyan-400/30 animate-pulse shadow-lg shadow-cyan-400/20"></div>
            <div className="absolute top-0 right-1/4 w-1 h-full bg-gradient-to-b from-purple-400/30 via-transparent to-purple-400/30 animate-pulse shadow-lg shadow-purple-400/20" style={{animationDelay: '1s'}}></div>
            <div className="absolute top-0 left-3/4 w-1 h-full bg-gradient-to-b from-orange-400/25 via-transparent to-orange-400/25 animate-pulse shadow-lg shadow-orange-400/15" style={{animationDelay: '2s'}}></div>
            
            {/* Enhanced corner accent elements */}
            <div className="absolute top-0 left-0 w-32 h-32">
              <div className="absolute top-0 left-0 w-16 h-1 bg-gradient-to-r from-cyan-400/60 to-transparent shadow-md shadow-cyan-400/40"></div>
              <div className="absolute top-0 left-0 w-1 h-16 bg-gradient-to-b from-cyan-400/60 to-transparent shadow-md shadow-cyan-400/40"></div>
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-cyan-400/50"></div>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32">
              <div className="absolute top-0 right-0 w-16 h-1 bg-gradient-to-l from-purple-400/60 to-transparent shadow-md shadow-purple-400/40"></div>
              <div className="absolute top-0 right-0 w-1 h-16 bg-gradient-to-b from-purple-400/60 to-transparent shadow-md shadow-purple-400/40"></div>
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-purple-400/50"></div>
            </div>
            <div className="absolute bottom-0 left-0 w-32 h-32">
              <div className="absolute bottom-0 left-0 w-16 h-1 bg-gradient-to-r from-orange-400/60 to-transparent shadow-md shadow-orange-400/40"></div>
              <div className="absolute bottom-0 left-0 w-1 h-16 bg-gradient-to-t from-orange-400/60 to-transparent shadow-md shadow-orange-400/40"></div>
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-orange-400/50"></div>
            </div>
            <div className="absolute bottom-0 right-0 w-32 h-32">
              <div className="absolute bottom-0 right-0 w-16 h-1 bg-gradient-to-l from-cyan-400/60 to-transparent shadow-md shadow-cyan-400/40"></div>
              <div className="absolute bottom-0 right-0 w-1 h-16 bg-gradient-to-t from-cyan-400/60 to-transparent shadow-md shadow-cyan-400/40"></div>
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-cyan-400/50"></div>
            </div>

            {/* Cyberpunk data streams */}
            <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent animate-pulse"></div>
            <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-400/30 to-transparent animate-pulse" style={{animationDelay: '1s'}}></div>
            <div className="absolute bottom-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent animate-pulse" style={{animationDelay: '2s'}}></div>
            
            {/* Enhanced glowing orbs */}
            <div className="absolute top-1/6 left-1/6 w-80 h-80 bg-gradient-to-r from-cyan-400/15 to-purple-400/15 rounded-full blur-3xl"></div>
            <div className="absolute top-2/3 right-1/6 w-80 h-80 bg-gradient-to-r from-purple-400/15 to-orange-400/15 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/6 left-1/3 w-96 h-96 bg-gradient-to-r from-orange-400/10 to-cyan-400/10 rounded-full blur-3xl"></div>
            
            {/* Additional atmospheric layers */}
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-300/5 via-transparent to-purple-400/8 pointer-events-none"></div>
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-orange-200/3 to-transparent pointer-events-none"></div>
          </div>

          {/* Background Overlay - adjusted opacity to show cyberpunk effects better */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/95"></div>
          
          {/* Original Atmospheric Effects - reduced for balance */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-r from-cyan-500/5 to-purple-600/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-r from-purple-600/5 to-pink-500/5 rounded-full blur-3xl"></div>
          </div>

          <div className="container mx-auto px-12 relative z-10">
            {/* Desktop Layout (lg+) - Original Layout */}
            <div className="hidden lg:grid lg:grid-cols-2 gap-8 items-center min-h-[80vh]">
              
              {/* Left Content */}
              <div className="space-y-8 pl-4">
                {/* Studio & Awards */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-4">
                    <div className="px-4 py-2 bg-gradient-to-r from-slate-700/50 to-slate-600/50 backdrop-blur-sm border border-cyan-500/30 rounded-full">
                      <span className="text-cyan-400 text-sm tracking-wide">{game.studio}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-6 text-sm">
                    {game.emmyNominees ? (
                      <span className="text-orange-400 font-medium">{game.emmyNominees}</span>
                    ) : null}
                    {typeof game.rating === 'number' && (
                      <span className="text-yellow-400 flex items-center">
                        <Star className="h-4 w-4 mr-1 fill-current" />
                        {game.imdbRating}
                      </span>
                    )}
                  </div>
                </div>

                {/* Main Title */}
                <div className="space-y-4">
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-none tracking-tight">
                    {(game.titleLines ?? game.title.split(' ')).map((word, wordIndex) => (
                      <div key={wordIndex} className="block">
                        {word}
                      </div>
                    ))}
                  </h1>
                  <div className="text-lg text-gray-300 tracking-wide max-w-md">
                    {game.subtitle}
                  </div>
                </div>

                {/* Quote */}
                {game.quote && game.quoteAuthor ? (
                  <div className="border-l-4 border-cyan-400 pl-6 py-4 max-w-lg">
                    <blockquote className="text-xl text-gray-200 italic mb-2">{game.quote}</blockquote>
                    <cite className="text-cyan-400 text-sm font-medium">{game.quoteAuthor}</cite>
                  </div>
                ) : null}

                {/* Status & Description */}
                <div className="space-y-4">
                  <div className="inline-block">
                    {game.statusLink ? (
                      <a 
                        href={game.statusLink}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-block transition-transform hover:scale-105"
                      >
                        <div className={`px-4 py-2 bg-gradient-to-r ${game.statusColor} rounded-full`}>
                          <span className="text-white text-sm font-medium tracking-wide">
                            {game.status.toUpperCase()}
                          </span>
                        </div>
                      </a>
                    ) : (
                      <div className={`px-4 py-2 bg-gradient-to-r ${game.statusColor} rounded-full`}>
                        <span className="text-white text-sm font-medium tracking-wide">
                          {game.status.toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  
                  <div className="max-w-2xl">
                    <p className="text-gray-300 text-lg leading-relaxed mb-4">
                      {game.fullDescription}
                    </p>
                    <p className="text-gray-400 leading-relaxed">
                      {game.description}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                {false && (
                  <div className="flex flex-wrap gap-4 pt-4">
                    <Button 
                      size="lg"
                      className={`bg-gradient-to-r ${game.accentColor} hover:scale-105 text-white px-8 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl`}
                    >
                      <Play className="h-5 w-5 mr-2 fill-current" />
                      {game.status === 'Coming Soon' ? 'Pre-Order Now' : 'Play Now'}
                    </Button>
                    
                    <Button 
                      variant="outline"
                      size="lg"
                      className="border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 px-8 py-4 rounded-full transition-all duration-300"
                    >
                      Learn More
                      <ExternalLink className="h-5 w-5 ml-2" />
                    </Button>
                  </div>
                )}

                {/* Game Stats */}
                <div className="flex items-center space-x-8 pt-4 border-t border-gray-700/50">
                  {typeof game.rating === 'number' && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white">{game.rating}</div>
                      <div className="text-sm text-gray-400">Rating</div>
                    </div>
                  )}
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">{game.genre}</div>
                    <div className="text-sm text-gray-400">{game.genreLabel ?? 'Genre'}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-cyan-400">{game.release}</div>
                    <div className="text-sm text-gray-400">{game.releaseLabel ?? 'Release'}</div>
                  </div>
                </div>
              </div>

              {/* Right Content - Game Image/Visual (Desktop Only) */}
              <div className="relative lg:ml-8 pr-4">
                <div className="relative">
                  {/* Main Game Image */}
                  <div className="relative overflow-hidden rounded-2xl border border-cyan-500/20 shadow-2xl transform hover:scale-105 transition-transform duration-500">
                    <div className="aspect-[3/4]">
                      <ImageWithFallback
                        src={game.image}
                        alt={game.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>
                    
                    {/* Floating Status Badge */}
                    <div className="absolute bottom-6 right-6">
                      <div className={`px-4 py-2 bg-gradient-to-r ${game.statusColor} backdrop-blur-sm rounded-full shadow-lg`}>
                        <span className="text-white font-medium text-sm">
                          {game.status}
                        </span>
                      </div>
                    </div>

                    {/* Rating Badge */}
                    {typeof game.rating === 'number' && (
                      <div className="absolute bottom-6 left-6">
                        <div className="flex items-center space-x-2 px-4 py-2 bg-black/70 backdrop-blur-sm rounded-full">
                          <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          <span className="text-white font-medium">{game.rating}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Enhanced Decorative Elements with Cyberpunk styling */}
                  <div className={`absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-r ${game.accentColor} rounded-full opacity-20 blur-xl`}></div>
                  <div className={`absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-r ${game.accentColor} rounded-full opacity-10 blur-2xl`}></div>
                  
                  {/* Cyberpunk corner accents for game images */}
                  <div className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-cyan-400/40"></div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-purple-400/40"></div>
                  <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-orange-400/40"></div>
                  <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-cyan-400/40"></div>
                </div>
              </div>
            </div>

            {/* Mobile Layout (below lg) - Reorganized Layout */}
            <div className="lg:hidden space-y-8 py-16 min-h-[90vh] flex flex-col justify-center px-4">
              
              {/* Studio & Awards */}
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <div className="px-4 py-2 bg-gradient-to-r from-slate-700/50 to-slate-600/50 backdrop-blur-sm border border-cyan-500/30 rounded-full">
                    <span className="text-cyan-400 text-sm tracking-wide">{game.studio}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-6 text-sm">
                  {game.emmyNominees ? (
                    <span className="text-orange-400 font-medium">{game.emmyNominees}</span>
                  ) : null}
                  {typeof game.rating === 'number' && (
                    <span className="text-yellow-400 flex items-center">
                      <Star className="h-4 w-4 mr-1 fill-current" />
                      {game.imdbRating}
                    </span>
                  )}
                </div>
              </div>

              {/* Main Title & Subtitle */}
              <div className="space-y-4">
                <h1 className="text-3xl md:text-4xl font-black text-white leading-none tracking-tight">
                  {(game.titleLines ?? game.title.split(' ')).map((word, wordIndex) => (
                    <div key={wordIndex} className="block">
                      {word}
                    </div>
                  ))}
                </h1>
                <div className="text-base sm:text-lg text-gray-300 tracking-wide leading-relaxed">
                  {game.subtitle}
                </div>
              </div>

              {/* Mobile Game Image - Now appears after title/subtitle */}
              <div className="relative max-w-xs mx-auto">
                <div className="relative">
                  {/* Main Game Image */}
                  <div className="relative overflow-hidden rounded-2xl border border-cyan-500/20 shadow-2xl">
                    <div className="aspect-[3/4]">
                      <ImageWithFallback
                        src={game.image}
                        alt={game.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>
                    
                    {/* Floating Status Badge */}
                    <div className="absolute bottom-4 right-4">
                    <div className={`px-3 py-1 bg-gradient-to-r ${game.statusColor} backdrop-blur-sm rounded-full shadow-lg`}>
                        <span className="text-white font-medium text-xs">
                        {game.status}
                        </span>
                      </div>
                    </div>

                    {/* Rating Badge */}
                    {typeof game.rating === 'number' && (
                      <div className="absolute bottom-4 left-4">
                        <div className="flex items-center space-x-1 px-3 py-1 bg-black/70 backdrop-blur-sm rounded-full">
                          <Star className="h-3 w-3 text-yellow-400 fill-current" />
                          <span className="text-white font-medium text-xs">{game.rating}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Enhanced Decorative Elements with Cyberpunk styling */}
                  <div className={`absolute -top-3 -right-3 w-16 h-16 bg-gradient-to-r ${game.accentColor} rounded-full opacity-20 blur-xl`}></div>
                  <div className={`absolute -bottom-3 -left-3 w-20 h-20 bg-gradient-to-r ${game.accentColor} rounded-full opacity-10 blur-2xl`}></div>
                  
                  {/* Cyberpunk corner accents for game images */}
                  <div className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 border-cyan-400/40"></div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2 border-purple-400/40"></div>
                  <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2 border-orange-400/40"></div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 border-cyan-400/40"></div>
                </div>
              </div>

              {/* Quote - Now appears after the image */}
              {game.quote && game.quoteAuthor ? (
                <div className="border-l-4 border-cyan-400 pl-6 py-4">
                  <blockquote className="text-lg text-gray-200 italic mb-2">{game.quote}</blockquote>
                  <cite className="text-cyan-400 text-sm font-medium">{game.quoteAuthor}</cite>
                </div>
              ) : null}

              {/* Status & Description */}
              <div className="space-y-4">
                <div className="inline-block">
                  {game.statusLink ? (
                    <a 
                      href={game.statusLink}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-block transition-transform hover:scale-105"
                    >
                      <div className={`px-4 py-2 bg-gradient-to-r ${game.statusColor} rounded-full`}>
                        <span className="text-white text-sm font-medium tracking-wide">
                          {game.status.toUpperCase()}
                        </span>
                      </div>
                    </a>
                  ) : (
                    <div className={`px-4 py-2 bg-gradient-to-r ${game.statusColor} rounded-full`}>
                      <span className="text-white text-sm font-medium tracking-wide">
                        {game.status.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                
                <div>
                  <p className="text-gray-300 text-base leading-relaxed mb-4">
                    {game.fullDescription}
                  </p>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {game.description}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              {false && (
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button 
                    size="lg"
                    className={`w-full sm:w-auto bg-gradient-to-r ${game.accentColor} hover:scale-105 text-white px-8 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl`}
                  >
                    <Play className="h-5 w-5 mr-2 fill-current" />
                    {game.status === 'Coming Soon' ? 'Pre-Order Now' : 'Play Now'}
                  </Button>
                  
                  <Button 
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 px-8 py-4 rounded-full transition-all duration-300"
                  >
                    Learn More
                    <ExternalLink className="h-5 w-5 ml-2" />
                  </Button>
                </div>
              )}

              {/* Game Stats */}
              <div className="flex items-center justify-center space-x-8 pt-4 border-t border-gray-700/50">
                {typeof game.rating === 'number' && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-white">{game.rating}</div>
                    <div className="text-xs text-gray-400">Rating</div>
                  </div>
                )}
                <div className="text-center">
                  <div className="text-xl font-bold text-white">{game.genre}</div>
                  <div className="text-xs text-gray-400">{game.genreLabel ?? 'Genre'}</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-cyan-400">{game.release}</div>
                  <div className="text-xs text-gray-400">{game.releaseLabel ?? 'Release'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll Indicator (only on first game) */}
          {index === 0 && (
            <div className="absolute bottom-8 left-0 right-0 flex justify-center animate-bounce">
              <div className="flex flex-col items-center space-y-2">
                <span className="text-gray-400 text-sm">Scroll to explore</span>
                <ChevronDown className="h-6 w-6 text-cyan-400" />
              </div>
            </div>
          )}
        </div>
      ))}

    </section>
  );
}